import { useState, useEffect, useCallback } from 'react';
import { vitalsEnhancedAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Activity, AlertTriangle, Plus, TrendingUp, Heart, Thermometer, Wind, Droplet } from 'lucide-react';

const VitalsPage = () => {
  const { hospital } = useAuth();
  const hospitalId = hospital?.id;
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [history, setHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showRecord, setShowRecord] = useState(false);

  const loadAlerts = useCallback(async () => {
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const res = await vitalsEnhancedAPI.alerts(params);
      setAlerts(res.data || []);
    } catch { /* silent */ }
  }, [hospitalId]);

  const loadPatients = useCallback(async () => {
    try {
      const params = hospitalId ? { hospital_id: hospitalId, limit: 200 } : { limit: 200 };
      const res = await patientsAPI.list(params);
      setPatients(res.data || []);
    } catch { /* silent */ }
  }, [hospitalId]);

  useEffect(() => { loadAlerts(); loadPatients(); }, [loadAlerts, loadPatients]);

  const loadHistory = useCallback(async () => {
    if (!selectedPatient) { setHistory([]); return; }
    try {
      const res = await vitalsEnhancedAPI.history(selectedPatient, 30);
      setHistory(res.data || []);
    } catch { toast.error('Failed to load history'); }
  }, [selectedPatient]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const latest = history[0];

  return (
    <div className="space-y-5" data-testid="vitals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vitals Monitoring</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track patient vitals with automated abnormal alerts</p>
        </div>
        <Button onClick={() => setShowRecord(true)} disabled={!selectedPatient} className="bg-teal-600 hover:bg-teal-700" data-testid="record-vitals-btn">
          <Plus size={16} className="mr-1.5" /> Record Vitals
        </Button>
      </div>

      {/* Active Alerts section removed per product feedback — alerts are now surfaced
          inline on each patient's vitals card and on the patient detail screen.
          Keeping the alerts state for future use elsewhere (e.g., command center). */}

      {/* Patient Select */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <Label>Select Patient</Label>
        <Select value={selectedPatient} onValueChange={setSelectedPatient}>
          <SelectTrigger className="mt-1.5" data-testid="patient-picker"><SelectValue placeholder="Choose patient..." /></SelectTrigger>
          <SelectContent>
            {patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} · {p.uhid || 'No UHID'}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selectedPatient && (
        <>
          {/* Latest vitals dashboard */}
          {latest ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <VitalCard icon={Heart} label="BP" value={latest.blood_pressure_systolic ? `${latest.blood_pressure_systolic}/${latest.blood_pressure_diastolic}` : '—'} unit="mmHg" alert={latest.blood_pressure_systolic > 140} />
              <VitalCard icon={Activity} label="Heart Rate" value={latest.heart_rate || latest.pulse_rate || '—'} unit="bpm" alert={(latest.heart_rate || latest.pulse_rate) > 100 || ((latest.heart_rate || latest.pulse_rate) > 0 && (latest.heart_rate || latest.pulse_rate) < 60)} />
              <VitalCard icon={Thermometer} label="Temp" value={latest.temperature || '—'} unit="°C" alert={latest.temperature > 38.5} />
              <VitalCard icon={Wind} label="SpO2" value={latest.spo2 || latest.oxygen_saturation || '—'} unit="%" alert={(latest.spo2 || latest.oxygen_saturation) > 0 && (latest.spo2 || latest.oxygen_saturation) < 94} />
              <VitalCard icon={Droplet} label="Blood Sugar" value={latest.blood_sugar || '—'} unit="mg/dL" alert={latest.blood_sugar > 200} />
              <VitalCard icon={Activity} label="Resp Rate" value={latest.respiratory_rate || '—'} unit="/min" />
              <VitalCard icon={Activity} label="Weight" value={latest.weight || '—'} unit="kg" />
              <VitalCard icon={Activity} label="BMI" value={latest.bmi || '—'} unit="" />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-dashed border-slate-200 p-12 text-center text-slate-400">
              No vitals recorded yet for this patient.
            </div>
          )}

          {/* History Table */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><TrendingUp size={16} /> History ({history.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-slate-500">
                    <th className="text-left py-2 px-2">Recorded</th>
                    <th className="text-left py-2 px-2">BP</th>
                    <th className="text-left py-2 px-2">HR</th>
                    <th className="text-left py-2 px-2">Temp</th>
                    <th className="text-left py-2 px-2">SpO2</th>
                    <th className="text-left py-2 px-2">Sugar</th>
                    <th className="text-left py-2 px-2">Alerts</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(v => (
                    <tr key={v.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-2 px-2 text-slate-600 text-xs">{new Date(v.recorded_at).toLocaleString()}</td>
                      <td className="py-2 px-2">{v.blood_pressure_systolic ? `${v.blood_pressure_systolic}/${v.blood_pressure_diastolic}` : '—'}</td>
                      <td className="py-2 px-2">{v.heart_rate || v.pulse_rate || '—'}</td>
                      <td className="py-2 px-2">{v.temperature || '—'}</td>
                      <td className="py-2 px-2">{v.spo2 || v.oxygen_saturation || '—'}</td>
                      <td className="py-2 px-2">{v.blood_sugar || '—'}</td>
                      <td className="py-2 px-2">
                        {(v.alerts || []).map((a, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 mr-1">{a.type}</span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <RecordModal open={showRecord} onClose={() => setShowRecord(false)} patientId={selectedPatient} hospitalId={hospitalId} onSaved={() => { loadHistory(); loadAlerts(); }} />
    </div>
  );
};

const VitalCard = ({ icon: Icon, label, value, unit, alert }) => (
  <div className={`rounded-xl border p-4 ${alert ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white'}`}>
    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5"><Icon size={13} />{label}</div>
    <p className={`text-xl font-bold ${alert ? 'text-red-700' : 'text-slate-800'}`}>{value} <span className="text-xs font-normal text-slate-400">{unit}</span></p>
  </div>
);

const RecordModal = ({ open, onClose, patientId, hospitalId, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({}); }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const numFields = ['blood_pressure_systolic', 'blood_pressure_diastolic', 'heart_rate', 'temperature', 'respiratory_rate', 'spo2', 'weight', 'height', 'blood_sugar', 'pain_scale'];
      const payload = { patient_id: patientId, hospital_id: hospitalId };
      numFields.forEach(f => { if (form[f]) payload[f] = parseFloat(form[f]); });
      if (form.notes) payload.notes = form.notes;
      if (payload.weight && payload.height) payload.bmi = +(payload.weight / Math.pow(payload.height / 100, 2)).toFixed(1);
      await vitalsEnhancedAPI.record(payload);
      toast.success('Vitals recorded');
      onSaved(); onClose();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Record Vitals</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
          <div><Label>BP Systolic</Label><Input type="number" placeholder="120" onChange={e => setForm({ ...form, blood_pressure_systolic: e.target.value })} data-testid="bp-sys" /></div>
          <div><Label>BP Diastolic</Label><Input type="number" placeholder="80" onChange={e => setForm({ ...form, blood_pressure_diastolic: e.target.value })} /></div>
          <div><Label>Heart Rate</Label><Input type="number" placeholder="72" onChange={e => setForm({ ...form, heart_rate: e.target.value })} /></div>
          <div><Label>Temperature (°C)</Label><Input type="number" step="0.1" placeholder="37.0" onChange={e => setForm({ ...form, temperature: e.target.value })} /></div>
          <div><Label>SpO2 (%)</Label><Input type="number" placeholder="98" onChange={e => setForm({ ...form, spo2: e.target.value })} /></div>
          <div><Label>Resp Rate</Label><Input type="number" placeholder="16" onChange={e => setForm({ ...form, respiratory_rate: e.target.value })} /></div>
          <div><Label>Weight (kg)</Label><Input type="number" step="0.1" onChange={e => setForm({ ...form, weight: e.target.value })} /></div>
          <div><Label>Height (cm)</Label><Input type="number" onChange={e => setForm({ ...form, height: e.target.value })} /></div>
          <div><Label>Blood Sugar (mg/dL)</Label><Input type="number" onChange={e => setForm({ ...form, blood_sugar: e.target.value })} /></div>
          <div><Label>Pain Scale (0-10)</Label><Input type="number" min="0" max="10" onChange={e => setForm({ ...form, pain_scale: e.target.value })} /></div>
        </div>
        <div><Label className="mt-2 block">Notes</Label><Input onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-vitals-btn">{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VitalsPage;
